// apps/client/src/hooks/useSubscriptionTier.ts
// Hook to retrieve and manage subscription tier information

import { useMemo } from 'react';
import { useAppContext } from '../components/layout/AppLayout';
import {
  SubscriptionTier,
  Feature,
  tierHasFeature,
  getUpgradeTier,
  FEATURE_REGISTRY,
} from '../config/subscriptionTiers';

interface UseSubscriptionTierResult {
  /** Current subscription tier */
  tier: SubscriptionTier;
  /** Whether the subscription is active */
  isActive: boolean;
  /** Check if a feature is available on the current tier */
  hasFeature: (feature: Feature) => boolean;
  /** Get the required tier for a feature */
  getRequiredTier: (feature: Feature) => SubscriptionTier;
  /** Loading state */
  loading: boolean;
}

/**
 * Hook to access subscription tier information and check feature availability
 *
 * Usage:
 * ```tsx
 * const { tier, hasFeature } = useSubscriptionTier();
 *
 * if (hasFeature('office_dashboard')) {
 *   return <OfficeDashboard />;
 * }
 * ```
 */
/**
 * @param companyDataOverride Company doc to gate against, for callers that render
 *   OUTSIDE the `<Outlet context={...}>` subtree and therefore cannot read the app
 *   context. `useAppContext()` is `useOutletContext()`, and the sidebars
 *   (MorphingSidebar, SideNav) are siblings of the `<Outlet>` inside AppLayout, not
 *   descendants, so the context is `null` for them and every feature-gated nav item
 *   would silently collapse to the Plus tier. Those components already receive the
 *   company doc as a prop; they pass it here so there is exactly ONE implementation
 *   of the precedence rules (disabled -> explicit deny -> additive grant -> allowlist
 *   -> active-subscription -> tier) instead of a drifting local copy per component.
 *   Omit it inside the Outlet subtree, where the context is the source of truth.
 */
export function useSubscriptionTier(companyDataOverride?: any): UseSubscriptionTierResult {
  const context = useAppContext();
  const companyData = companyDataOverride !== undefined ? companyDataOverride : context?.companyData;

  const result = useMemo(() => {
    // Extract tier from company subscription data
    // The tier field can be: 'plus', 'pro', 'enterprise'
    // Legacy data might have 'monthly', 'yearly' - treat as 'plus' (basic tier)
    const rawTier = companyData?.subscription?.tier?.toLowerCase() || '';

    // Map subscription tier - handle legacy values
    let tier: SubscriptionTier = 'plus'; // Default to plus
    if (rawTier === 'pro' || rawTier === 'professional') {
      tier = 'pro';
    } else if (rawTier === 'enterprise' || rawTier === 'business') {
      tier = 'enterprise';
    }

    // Check if subscription is active
    // GRACE_PERIOD is treated as active — dunning email promises 7 days of full access
    const subscriptionState = companyData?.subscription?.state || 'active';
    const isActive = subscriptionState === 'active' || subscriptionState === 'trialing' || subscriptionState === 'grace_period';

    return {
      tier,
      isActive,
      loading: false,
    };
  }, [companyData?.subscription]);

  // Feature check function - combines tier check with active subscription + company allowlist
  const hasFeature = (feature: Feature): boolean => {
    const featureConfig = FEATURE_REGISTRY[feature];

    // Disabled features are always unavailable (parked — data integrity pending)
    if (featureConfig?.disabled) return false;

    // Per-company DENY: `featureFlags[feature] === false` on the company doc
    // blocks the feature, beating the tier grant, the add-on grant and the promo
    // grant. This mirrors the backend checker, which has always denied this way
    // (root CLAUDE.md s.9 / services/client-functions/CLAUDE.md), and closes a
    // real client/server drift: the client honoured only the `=== true` additive
    // grant below, so a tenant that had explicitly switched a feature OFF still
    // reached the page client-side and only the nav respected the flag. The deny
    // is per-constituent, exactly like the server: FeatureGate's `anyOf` union
    // still passes when the OTHER constituent is granted, so denying one half of
    // a union cannot single-handedly disable it.
    if (companyData?.featureFlags?.[feature] === false) return false;

    // Per-company override (additive grant): `featureFlags[feature] === true` on the company
    // doc unlocks the feature regardless of tier. Mirrors the backend `featureGate.ts` check
    // so a Firestore write alone can grant a feature without a code deploy. Checked BEFORE
    // allowedCompanyIds so a Firestore grant surfaces an allowlist-gated feature
    // (loyalty_program) for demo tenants without editing the compiled allowlist.
    if (companyData?.featureFlags?.[feature] === true) return true;

    // Company allowlist: if set, only listed companies can access (trial/rollout gating)
    if (featureConfig?.allowedCompanyIds) {
      const companyId = companyData?.id || companyData?.companyId;
      if (!companyId || !featureConfig.allowedCompanyIds.includes(companyId)) return false;
    }

    // If subscription is not active, only allow plus tier features
    if (!result.isActive) {
      const requiredTier = featureConfig?.requiredTier;
      return requiredTier === 'plus';
    }

    return tierHasFeature(result.tier, feature);
  };

  // Get required tier for a feature
  const getRequiredTier = (feature: Feature): SubscriptionTier => {
    return getUpgradeTier(feature);
  };

  return {
    tier: result.tier,
    isActive: result.isActive,
    hasFeature,
    getRequiredTier,
    loading: result.loading,
  };
}
